// components/EnlargeableImage.tsx
import React, { ImgHTMLAttributes, useEffect, useState } from "react";

import EnlargeImageModal from "./EnlargeImageModal";
import { graphql } from "@helpers/graphql";

import useSubmitting from "@helpers/hooks/useSubmitting";
import { Spinner } from "../Spinner";
import { NEXT_PUBLIC_S3_BUCKET_PATH } from "@constants";

type EnlargeableImageProps = {
  class?: ImgHTMLAttributes<HTMLImageElement>["className"];
} & ImgHTMLAttributes<HTMLImageElement>;

const EnlargeableImage: React.FC<EnlargeableImageProps> = ({ ...props }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setSubmitting] = useSubmitting(false);

  const [highQualityImage, setHighQualityImage] = useState("");

  const closeModal = () => {
    setIsModalOpen(false);
    setHighQualityImage("");
  };

  const getHighQualityImage = () => {
    setSubmitting(true);
    graphql
      .getHighQualityImageFromUrl({
        url: props.src || "",
      })
      .then((image) => {
        setIsModalOpen(true);
        setHighQualityImage(image.getHighQualityImageFromUrl);
        setSubmitting(false);
      })
      .catch(() => {
        setIsModalOpen(true);
        setSubmitting(false);
        setHighQualityImage(props.src || "");
      });
  };

  const [isImageLoading, setIsImageLoading] = useState(true);

  const handleImageLoad = () => {
    setIsImageLoading(false);
  };

  const newPropsClass = props["class"] || props.className;
  props["class"] = undefined;
  props.className = undefined;
  props.alt = props.alt || "";

  const [displaySrc, setDisplaySrc] = useState(props.src || "");

  const getOriginalUrl = () => {
    //http://localhost:3000/blog/undefined/filters:autojpg()/fit-in/160x160/gowithguide/profiles/32287/107588.png
    //Fallback to original source if imgproxy fails due big image timeout from CDN to imgproxy.
    // We can use only s3 path directly as we re getting timeout via CDN
    if (props.src && props.src.includes("/gowithguide/")) {
      const originalUrl = `${NEXT_PUBLIC_S3_BUCKET_PATH}/gowithguide/${props.src.split("/gowithguide/")[1]}`;
      console.log(`originalUrl :`, originalUrl);
      return originalUrl;
    }
    return props.src;
  };

  // Preload optimized (imgproxy) version and switch if successful
  useEffect(() => {
    if (!props.src) return;

    const proxyImg = new Image();
    proxyImg.src = props.src;

    // Show imgproxy image if it loads
    proxyImg.onload = () => {
      setDisplaySrc(props.src!);
    };

    // Use original image if proxy fails
    proxyImg.onerror = () => {
      const original = getOriginalUrl();
      if (original) {
        setDisplaySrc(original);
      }
    };
  }, [props.src]);

  return (
    <>
      {isSubmitting && (
        <div className="fixed z-0 top-1/2 left-1/2">
          <Spinner text="" />
        </div>
      )}

      <img
        {...props}
        onClick={() => {
          getHighQualityImage();
        }}
        className={`cursor-zoom-in ${newPropsClass}`}
        src={displaySrc}
      />

      {isModalOpen && !isSubmitting && (
        <EnlargeImageModal visible={isModalOpen} onClose={closeModal}>
          <div className="fixed z-0 top-1/2 left-1/2">
            {isImageLoading && <Spinner text="" />}
          </div>

          {/* High quality image opened from blog */}
          <div className="w-full lg:w-2/3 h-auto">
            <img
              src={highQualityImage || props.src}
              alt={props.alt}
              loading="lazy"
              onClick={closeModal}
              className="w-full h-auto cursor-zoom-out"
              onLoad={handleImageLoad}
              onError={() => {
                setHighQualityImage(props.src || "");
              }}
            />
          </div>
        </EnlargeImageModal>
      )}
    </>
  );
};

export default EnlargeableImage;
